// src/hrms/alarms/useAlarms.ts
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useAuth } from "../contexts/AuthContext";
import { subscribeAlarmClock } from "./alarmEngine";
import { applyAlarmSettings } from "./alarmModel";
import { syncAlarmItems, type AlarmTrackedItem } from "./alarmItems";
import { loadAlarms, subscribeAlarms, updateAlarm, type AlarmMap } from "./alarmStore";
import type { AlarmItemKey, AlarmItemSnapshot, AlarmSettings, ItemAlarm } from "./alarmTypes";

/** Whose alarms this tab shows: the signed-in user's, or nobody's. */
export function useAlarmOwner(): string | null {
  const { user } = useAuth();
  return user?.id ? String(user.id) : null;
}

export function useAlarmMap(): { alarms: AlarmMap; ownerId: string | null } {
  const ownerId = useAlarmOwner();
  const getSnapshot = useCallback(() => loadAlarms(ownerId), [ownerId]);
  const alarms = useSyncExternalStore(subscribeAlarms, getSnapshot, getSnapshot);
  return { alarms, ownerId };
}

export function useItemAlarm(itemKey: AlarmItemKey | null): { alarm: ItemAlarm | undefined; ownerId: string | null } {
  const { alarms, ownerId } = useAlarmMap();
  return { alarm: itemKey ? alarms[itemKey] : undefined, ownerId };
}

/** "Now", refreshed whenever the alarm engine wakes and at least every `intervalMs`. */
export function useAlarmClock(intervalMs = 30_000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const tick = () => setNow(Date.now());
    const unsubscribe = subscribeAlarmClock(tick);
    const id = setInterval(tick, intervalMs);
    return () => {
      unsubscribe();
      clearInterval(id);
    };
  }, [intervalMs]);
  return now;
}

/** Saves an item's alarm settings from a form. */
export function saveItemAlarm(ownerId: string, itemKey: AlarmItemKey, item: AlarmItemSnapshot, settings: AlarmSettings) {
  updateAlarm(ownerId, itemKey, (existing) => applyAlarmSettings(existing, item, settings, Date.now()));
}

/** Runs syncAlarmItems whenever a page's item list changes. */
export function useAlarmItemSync(items: AlarmTrackedItem[] | null) {
  const ownerId = useAlarmOwner();
  // The list is rebuilt on every render; only re-sync when what the store compares changes.
  const signature = useMemo(() => (items ? JSON.stringify(items) : ""), [items]);
  useEffect(() => {
    if (!ownerId || !items) return;
    syncAlarmItems(ownerId, items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId, signature]);
}
